import { useEffect, useMemo, useState } from 'react';
import { SearchIcon, PencilIcon, TrashIcon, PlusIcon, ChevronDownIcon } from '@bizfirst/common-react';
import type { AgentDTO } from '../../types';
import { useAgentAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { AgentEditor } from './AgentEditor';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

export interface AgentPickerProps {
  value?: number | null;
  onChange: (agentId: number | null, agent?: AgentDTO) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Show Create/Edit/Delete affordances. Defaults true — set false for a read-only picker. */
  allowManage?: boolean;
}

/**
 * Search/select/create/edit/delete for Agents — generalized from flow-studio-designer's
 * ConnectorConfigDialog.tsx inline Agent-tab markup (lines 1833-1919) into a standalone,
 * host-agnostic component. Delete is new here (see AgentAdminApiClient.deleteById's comment for
 * the confirmed frontend gap this closes).
 */
export function AgentPicker({ value, onChange, label = 'Agent', placeholder = 'Search agents...', disabled, allowManage = true }: AgentPickerProps) {
  const api = useAgentAdminApiClient();
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [editorState, setEditorState] = useState<{ mode: 'create' | 'edit'; agentId?: number } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedAgent = useMemo(() => agents.find((a) => a.AgentID === value) ?? null, [agents, value]);

  const loadAgents = async (openAfter = true) => {
    setLoading(true);
    const result = await api.getActive();
    setAgents(result.data ?? []);
    setLoading(false);
    if (openAfter) setShowResults(true);
  };

  useEffect(() => {
    // If a value is preselected but not yet in the local list (e.g. deep link), load once so the
    // selected agent's name can be shown instead of a bare ID. This must NOT open the dropdown —
    // it's a silent name resolution, not a user-initiated browse.
    if (value && !agents.some((a) => a.AgentID === value)) {
      loadAgents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const filtered = search.trim()
    ? agents.filter((a) => a.Name.toLowerCase().includes(search.toLowerCase()))
    : agents;

  const handleSelect = (agent: AgentDTO) => {
    onChange(agent.AgentID, agent);
    setSearch(agent.Name);
    setShowResults(false);
  };

  const handleEditorSaved = async (agent: AgentDTO) => {
    await loadAgents();
    setEditorState(null);
    onChange(agent.AgentID, agent);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteById(pendingDelete.AgentID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      await loadAgents();
      if (value === pendingDelete.AgentID) onChange(null);
    }
  };

  return (
    <div className="aaa-entity-picker">
      <label className="aaa-entity-label">{label}</label>
      <div className="aaa-entity-search-row">
        <div className="aaa-entity-search-input-wrap">
          <SearchIcon size={14} />
          <input
            type="text"
            placeholder={placeholder}
            value={search || selectedAgent?.Name || ''}
            disabled={disabled || loading}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowResults(true);
              if (!agents.length) loadAgents();
            }}
            onFocus={() => {
              if (!agents.length) loadAgents();
              else setShowResults(true);
            }}
          />
        </div>
        <button type="button" className="aaa-icon-btn aaa-entity-browse-btn" onClick={() => (showResults ? setShowResults(false) : loadAgents())} disabled={disabled} title="Browse agents">
          <ChevronDownIcon size={14} />
        </button>
        {allowManage && (
          <>
            <button
              type="button"
              className="aaa-icon-btn"
              disabled={disabled || !value}
              onClick={() => value && setEditorState({ mode: 'edit', agentId: value })}
              title="Edit agent"
            >
              <PencilIcon size={13} />
            </button>
            <button
              type="button"
              className="aaa-icon-btn aaa-icon-btn-danger"
              disabled={disabled || !value}
              onClick={() => selectedAgent && setPendingDelete(selectedAgent)}
              title="Delete agent"
            >
              <TrashIcon size={13} />
            </button>
            <button type="button" className="aaa-entity-create-btn" disabled={disabled} onClick={() => setEditorState({ mode: 'create' })} title="Add new agent">
              <PlusIcon size={13} /> Create
            </button>
          </>
        )}
      </div>

      {showResults && (
        <div className="aaa-entity-dropdown">
          {loading && <div className="aaa-entity-dropdown-empty">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="aaa-entity-dropdown-empty">No agents found.</div>}
          {!loading &&
            filtered.map((agent) => (
              <button
                type="button"
                key={agent.AgentID}
                className={`aaa-entity-dropdown-item${agent.AgentID === value ? ' selected' : ''}`}
                onClick={() => handleSelect(agent)}
              >
                <span>{agent.Name}</span>
                {agent.ShortDescription && <span className="aaa-entity-dropdown-item-hint">{agent.ShortDescription}</span>}
              </button>
            ))}
        </div>
      )}

      {editorState && (
        <AgentEditor
          agentId={editorState.mode === 'edit' ? editorState.agentId : undefined}
          onClose={() => setEditorState(null)}
          onSaved={handleEditorSaved}
        />
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Agent"
        message={`Delete "${pendingDelete?.Name}"? This soft-deletes the agent — it will no longer appear in search or active lists.`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
